import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { fitLifted } from '../../chrome';
import { Hint, Question, TextField, TextLink } from '../../components';
import { useT, type T } from '../../i18n';
import { useAirlineSearch, type Airline } from '../../lib/api';
import { formatMonth } from '../../lib/format';
import type { ManualValue } from './manual';
import './S3-manual.css';

export interface S3ManualProps {
  value: ManualValue;
  onChange: (v: ManualValue) => void;
  /** "We couldn't find VY6650 on 8 Sep 2026…", shown in place after a failed lookup. */
  message: string | null;
  /** "Back to the flight list"; omitted when there is no list (coverage none/unavailable…). */
  onBackToList?: () => void;
  /** Focus the airline input: when the visitor asked for manual entry, not when S3 fell back to it. */
  autoFocus?: boolean;
}

/**
 * S3-manual · "I can't find my flight", revealed in place on S3 (never its own URL).
 * Airline autocomplete (locks the two-character code) + the number. S3 owns the dock:
 * it looks the flight up on Continue and shows the result here.
 */
export default function S3Manual({ value, onChange, message, onBackToList, autoFocus }: S3ManualProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const numberId = `fn${useId()}`;
  const q = value.airline ? '' : value.airlineText;
  const search = useAirlineSearch(q);
  const rows = search.data ?? [];
  const listOpen = open && !!q.trim() && rows.length > 0;

  useEffect(() => {
    if (listOpen) fitLifted();
  }, [listOpen, rows.length]);

  function pick(a: Airline) {
    if (a.status === 'defunct') return; // Q11: shown, not selectable (DECISION-NEEDED)
    onChange({ ...value, airline: a, airlineText: `${a.name} (${a.iata})` });
    setOpen(false);
    setCursor(0);
    document.getElementById(numberId)?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!listOpen) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (e.key === 'ArrowDown' ? Math.min(c + 1, rows.length - 1) : Math.max(c - 1, 0)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(rows[Math.min(cursor, rows.length - 1)]!);
    } else if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div className="s3-manual" data-screen="S3-manual">
      <Question as="h2" small>
        {t('S3-manual.title')}
      </Question>
      <div className="pair">
        <TextField
          label={t('S3-manual.airline')}
          placeholder={t('S3-manual.airlinePlaceholder')}
          autoComplete="off"
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={listOpen}
          aria-autocomplete="list"
          filled={!!value.airline}
          value={value.airlineText}
          onChange={(e) => {
            onChange({ ...value, airline: null, airlineText: e.target.value });
            setOpen(true);
            setCursor(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        >
          {listOpen && (
            <div className="ac" role="listbox" data-lift-extra onMouseDown={(e) => e.preventDefault()}>
              {rows.map((a, i) => {
                const status = defunctStatus(t, a);
                return (
                  <button
                    key={`${a.iata}-${a.icao ?? ''}`}
                    type="button"
                    role="option"
                    aria-selected={i === cursor}
                    aria-disabled={status ? true : undefined}
                    aria-label={status ? `${a.name} (${a.iata}). ${status}` : `${a.name} (${a.iata})`}
                    className={`ac__row${i === cursor ? ' is-cursor' : ''}${status ? ' is-defunct' : ''}`}
                    onClick={() => pick(a)}
                  >
                    <span>
                      <span className="ac__city">
                        {a.name}
                        <b>{a.iata}</b>
                      </span>
                      {status && <span className="ac__status">{status}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </TextField>
        <TextField
          id={numberId}
          label={t('S3-manual.number')}
          placeholder={t('S3-manual.numberPlaceholder')}
          mono
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          autoComplete="off"
          value={value.number}
          onChange={(e) => onChange({ ...value, number: e.target.value.replace(/\D/g, '').slice(0, 4) })}
        />
      </div>
      {message && <Hint warn>{message}</Hint>}
      {onBackToList && (
        <TextLink quiet onClick={onBackToList}>
          {t('S3-manual.backToList')}
        </TextLink>
      )}
    </div>
  );
}

/**
 * A defunct airline's status line (Q11): "Stopped flying in October 2017", plus why its claims
 * can't be pursued when that is the case. Null for an active airline.
 */
function defunctStatus(t: T, a: Airline): string | null {
  if (a.status !== 'defunct') return null;
  const month = formatMonth(a.ceasedIn);
  const stopped = month ? t('S3-manual.defunct.stopped', { month }) : t('S3-manual.defunct.stoppedUnknown');
  return a.ceasedReason === 'insolvent' ? `${stopped}. ${t('S3-manual.defunct.insolvent')}` : stopped;
}
